/** @module Auth0dIdpConfig */
/* eslint-disable import/extensions */
import Auth0Utils from '../utils/auth0-utils-purejs.mjs';
import Auth0CdnResourceResolver from '../cdn/utils/auth0-cdn-resource-resolver.mst.mjs';
import {
  parseTenantName,
  getActiveTenantClassesForIdp,
  getAlwaysAvailableTenantClassesForIdp,
} from './auth0-tenant-registry.mjs';

/**
 * @constructor
 * @description A list of currently supported Identity Providers, information about those providers and several utility functions to assist in querying this data.
 * @param {string} tenantOrDomain The shipstation.com signin domain or the tenant (e.g. signin.shipstation.com, signin-stage.shipstation.com, shipstation-stage, etc)
 * @param {boolean} [includeTestIdps=false] Used only by the unit test runner and just exposes some test entires.
 */
class Auth0dIdpConfig {
  constructor(options) {
    const resolved = Auth0Utils.tenantOrDomainResolver(options.tenantOrDomain);
    this._tenant = resolved.tenant.id;
    this._tenantClass = this._getTenantClass(this._tenant);
    this._includeTestIdps = options.includeTestIdps;

    // CDN assets are environment-specific, NOT tenant-specific.
    // All tenant classes within an environment share the same CDN folder.
    // Use Auth0CdnResourceResolver which has the correct environment-based path
    // (via mustache template shipstation-stage replaced at build time).
    const { AUTH0_IMAGES_URL } = Auth0CdnResourceResolver;

    this.idps = [
      {
        name: 'Shopify - Single Seller',
        id: 'shopify',
        logoUrl: `${AUTH0_IMAGES_URL}/shopify.svg`,
        loginWithPopup: false,
        supportsFederatedLogout: false,
        active: getActiveTenantClassesForIdp('shopify'),
        alwaysAvailable: getAlwaysAvailableTenantClassesForIdp('shopify'),
      },
      {
        name: 'Shopify',
        isCustomAuth0Provider: true,
        id: 'shopify-oauth2',
        logoUrl: `${AUTH0_IMAGES_URL}/shopify.svg`,
        loginWithPopup: false,
        supportsFederatedLogout: false,
        active: getActiveTenantClassesForIdp('shopify-oauth2'),
        alwaysAvailable: getAlwaysAvailableTenantClassesForIdp('shopify-oauth2'),
        // TODO: update with actual new app
        // Note: adding "install" may actually start the install or launch automatically. This could be good, but also
        // may give up the Shopify app description where we could describe why user is seeing this page instead of just a Shopify login page.
        shopifyAppUrl: 'https://apps.shopify.com/shipstation/install',
      },
      {
        name: 'Google',
        id: 'google-oauth2',
        logoUrl: `${AUTH0_IMAGES_URL}/google.svg`,
        loginWithPopup: false,
        supportsFederatedLogout: true,
        active: getActiveTenantClassesForIdp('google-oauth2'),
        alwaysAvailable: getAlwaysAvailableTenantClassesForIdp('google-oauth2'),
      },
      {
        name: 'Woot',
        isCustomAuth0Provider: true,
        id: 'woot-oauth2',
        logoUrl: `${AUTH0_IMAGES_URL}/woot.svg`,
        loginWithPopup: false,
        supportsFederatedLogout: false,
        active: getActiveTenantClassesForIdp('woot-oauth2'),
        alwaysAvailable: getAlwaysAvailableTenantClassesForIdp('woot-oauth2'),
      },
    ];

    if (this._includeTestIdps) {
      this.idps.push({
        name: 'TestIdp',
        id: 'test-idp',
        logoUrl: `${AUTH0_IMAGES_URL}/test-idp.png`,
        loginWithPopup: false,
        active: getActiveTenantClassesForIdp('test-idp'),
        alwaysAvailable: getAlwaysAvailableTenantClassesForIdp('test-idp'),
      });
      this.idps.push({
        name: 'TestIdp Custom',
        id: 'test-idp-custom',
        logoUrl: `${AUTH0_IMAGES_URL}/test-idp.png`,
        loginWithPopup: false,
        isCustomAuth0Provider: true,
        active: getActiveTenantClassesForIdp('test-idp-custom'),
        alwaysAvailable: getAlwaysAvailableTenantClassesForIdp('test-idp-custom'),
      });
    }
  }

  /**
   * @private
   * @description Determines the tenant class from the tenant ID
   * @param {string} tenant The Auth0 tenant ID
   * @returns {string} The tenant class (e.g., 'shipstation', 'shipstation-partners')
   */
  _getTenantClass(tenant) {
    const parsed = parseTenantName(tenant);
    return parsed?.tenantClass ?? 'shipstation';
  }

  /**
   * @function
   * @description Returns all IdPs that are active for the given tenant class
   * @param {string} [tenantClass] The tenant class to filter by. If not provided, uses the tenant class from constructor.
   * @returns {array} Array of IdP objects that are active for the tenant class
   */
  getActiveIdps(tenantClass) {
    const targetClass = tenantClass || this._tenantClass;
    return this.idps.filter(idp => idp.active && idp.active.includes(targetClass));
  }

  /**
   * @function
   * @description Returns IdPs that should always be visible (Step 1 of progressive auth) for the given tenant class
   * @param {string} [tenantClass] The tenant class to filter by. If not provided, uses the tenant class from constructor.
   * @returns {array} Array of IdP objects that should always be visible for the tenant class
   */
  getAlwaysAvailableIdps(tenantClass) {
    const targetClass = tenantClass || this._tenantClass;
    return this.idps.filter(idp => idp.alwaysAvailable && idp.alwaysAvailable.includes(targetClass));
  }

  /**
   * @function
   * @description Checks if a specific IdP is active for the given tenant class
   * @param {string} idpId The IdP ID to check
   * @param {string} [tenantClass] The tenant class to check against. If not provided, uses the tenant class from constructor.
   * @returns {boolean} True if the IdP is active for the tenant class
   */
  isIdpActive(idpId, tenantClass) {
    const targetClass = tenantClass || this._tenantClass;
    const idp = this.idps.find(i => i.id === idpId);
    return idp && idp.active && idp.active.includes(targetClass);
  }
}

export default Auth0dIdpConfig;
